/**
 * Merge field computation utilities for template preview rendering.
 *
 * Supports both v2 ReadTemplateField and legacy PdfFormTemplateField types
 * via a minimal interface that covers both property name conventions.
 */

/** Minimal field shape for merge computation - compatible with both v2 and legacy types */
interface MergeableField {
  id: string;
  // v2 property names
  context_key?: string | null;
  default_value?: string | null;
  reference?: string;
  // Legacy property names (PdfFormTemplateField, DocxTemplateFieldSchema)
  field_source_context_key?: string | null;
  field_source_default_value?: string | null;
  field_reference?: string;
}

/** Try all possible override key conventions (v2: context_key/reference, legacy: field_source_context_key/field_reference, fallback: id) */
function findOverrideValue(
  field: MergeableField,
  overrides: Record<string, string>,
): string | undefined {
  const key = [
    field.context_key,
    field.field_source_context_key,
    field.reference,
    field.field_reference,
    field.id,
  ].find((k) => k && k in overrides && overrides[k] !== '');
  return key ? overrides[key] : undefined;
}

export const computePdfMergeFields = (
  fields: MergeableField[],
  matterContext: Record<string, string | undefined>,
  overrides: Record<string, string>,
): Record<string, string | undefined> => {
  return fields.reduce<Record<string, string | undefined>>((acc, field) => {
    const contextKey = field.context_key || field.field_source_context_key;
    const defaultValue = field.default_value || field.field_source_default_value;

    const overrideValue = findOverrideValue(field, overrides);
    if (overrideValue !== undefined) {
      acc[field.id] = overrideValue;
    } else if (contextKey && contextKey in matterContext) {
      acc[field.id] = matterContext[contextKey];
    } else if (defaultValue) {
      acc[field.id] = defaultValue;
    } else {
      acc[field.id] = undefined;
    }
    return acc;
  }, {});
};

/**
 * Value types that can be passed to DOCX template rendering.
 * Arrays and objects are used for Jinja2 iteration (e.g., {% for item in list %})
 */
export type DocxMergeFieldValue =
  | string
  | Array<{ name: string; address: string }>
  | Array<{ bodyPart: string; description: string }>
  | Array<Record<string, string>>
  | Record<string, string>
  | Record<string, unknown>
  | null;

export const computeDocxMergeFields = (
  fields: MergeableField[],
  matterContext: Record<string, DocxMergeFieldValue | undefined>,
  overrides: Record<string, string>,
): Record<string, DocxMergeFieldValue> => {
  return fields.reduce<Record<string, DocxMergeFieldValue>>((acc, field) => {
    const ref = field.reference || field.field_reference || field.id;
    const contextKey = field.context_key || field.field_source_context_key;

    const overrideValue = findOverrideValue(field, overrides);
    if (overrideValue !== undefined) {
      acc[ref] = overrideValue;
    } else if (contextKey && contextKey in matterContext) {
      const val = matterContext[contextKey];
      if (val !== undefined) {
        acc[ref] = val;
      }
    }
    return acc;
  }, {});
};
